import React from "react";
import { useTranslation } from "react-i18next";
import { useErrorBoundary } from "react-error-boundary";

import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Result, Button, Layout, Row, Space, Avatar, List } from "antd";

import { clientUrls } from "misc/urls/clientUrls";
import { useKeycloak } from "@react-keycloak/web";

import { TransparentButton } from "misc/buttons";
import { TextField } from "misc/fields";

import { stsColors } from "styles";
import { clearLocaleStorage } from "misc/helpfunctions";
import { Link } from "react-router-dom";
import { StreetScooterLogo } from "navigation/Sidebar/logo";
import { LanguageSelector } from "misc/selectors";

const StyledResult = styled(Result)({
    height: "100%",
    flexDirection: "column",
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
});

const ErrorIcon = styled(FontAwesomeIcon)({
    color: stsColors.grey2
});

const Title = styled(TextField)({
    color: stsColors.grey3
});

const ErrorSite = ({ error }) => {
    const { resetBoundary } = useErrorBoundary();
    const { keycloak } = useKeycloak();
    const [t] = useTranslation();
    const onLogout = () => {
        clearLocaleStorage();
        keycloak.logout();
    };

    const ErrorMappings = {
        default: {
            title: () => t("t_http_error_client_error"),
            subtitle: () => t("t_http_error_occurred"),
            icon: <ErrorIcon size="9x" icon={["fas", "car-crash"]} />
        },
        403: {
            title: () => t("t_result_title_unauthorized_access"),
            subtitle: () => t("t_result_sub_title_unauthorized_access"),
            icon: <ErrorIcon size="9x" icon={["fas", "user-lock"]} />
        },
        404: {
            title: () => t("t_result_title_not_found"),
            subtitle: () => t("t_result_sub_title_not_found"),
            icon: <ErrorIcon size="9x" icon={["fas", "search-location"]} />
        }
    };

    const getErrorContent = error => {
        // Determine the key for looking up the error
        const errorKey = error && typeof error === "object" ? error.status : "default";

        const { title, subtitle, icon } = ErrorMappings[errorKey] || ErrorMappings.default;

        return {
            title: title(),
            subtitle: subtitle(),
            icon
        };
    };

    const { title, subtitle, icon } = getErrorContent(error);

    return (
        <Layout>
            <Row justify="space-between" className="header" align="middle" style={{ padding: "25px 20px 20px 20px", background: "#f5f5f5" }}>
                <Link to={clientUrls.users.baseUrl()}>
                    <StreetScooterLogo height="50" />
                </Link>

                <Space size={15}>
                    <Space size={5}>
                        <Button onClick={onLogout}>{t("t_button_label_sign_out")}</Button>
                    </Space>
                </Space>
            </Row>
            <Layout>
                <StyledResult
                    status="error" // This can be dynamic based on error type if needed
                    icon={icon}
                    title={<Title value={title} />}
                    subTitle={<TextField value={subtitle} />}
                    extra={[
                        <TransparentButton size="large" onClick={resetBoundary}>
                            <TextField value="t_retry" />
                        </TransparentButton>
                    ]}
                />
            </Layout>
        </Layout>
    );
};

export default ErrorSite;
